import * as React from 'react';
import { Cross, ArrowDown } from '@sravni/react-icons';
import { useStyles, useFormControlContext, useBoolean, useStateCallback, useMergedRef } from '@sravni/react-utils';
import { Dropdown } from '../../Dropdown/src/Dropdown.js';
import { TextInput } from '../../TextInput/src/TextInput.js';
import modules_be025dea from '../styles/Select.module.less.js';

/**
 * @description The Select component allow user to select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Select placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
const Select = React.forwardRef((props, ref) => {
    const { autoFocus, defaultValue, value, mode, options, invalid, disabled, readOnly, icon, leftIcon, placement = 'bottom-start', fixedWidth = true, className, menuClassName, withCrossIcon = true, optionsSort = true, flip = true, mobile, fullscreen, onChange, onFocus, onBlur, ...restProps } = props;
    const cx = useStyles(modules_be025dea);
    const classNames = cx(className, 'container');
    const inputRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const formControlContext = useFormControlContext();
    const [visible, setVisible] = useBoolean(false);
    const [focused, setFocused] = useBoolean(false);
    const [innerValue, setInnerValue] = useStateCallback(value ?? defaultValue);
    const [innerOptions, setInnerOptions] = React.useState(options || []);
    const isMultiple = React.useMemo(() => mode === 'multiple', [mode]);
    const optionsMap = React.useMemo(() => {
        if (!innerOptions) {
            return {};
        }
        return innerOptions.reduce((acc, o) => {
            acc[o.value] = o;
            return acc;
        }, {});
    }, [innerOptions]);
    const displayValue = React.useMemo(() => {
        if (innerValue === undefined || innerValue === null) {
            return undefined;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            if (innerValue.length === 0) {
                return undefined;
            }
            if (innerOptions) {
                return innerValue
                    .map((v) => optionsMap[v]?.label)
                    .filter(Boolean)
                    .join(', ');
            }
        }
        return optionsMap[innerValue]?.label;
    }, [innerOptions, isMultiple, innerValue, optionsMap]);
    const setValue = React.useCallback((value) => {
        setInnerValue(value, (val) => {
            onChange?.(val ?? '');
        });
    }, [onChange, setInnerValue]);
    const hideMenu = React.useCallback(() => {
        setVisible.off();
        inputRef.current?.focus();
    }, [setVisible]);
    const handleSelect = React.useCallback((value) => {
        setValue(value);
        isMultiple ? inputRef.current?.focus() : hideMenu();
    }, [isMultiple, setValue, hideMenu]);
    const handleFocus = React.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React.useCallback((event) => {
        if (menuRef.current && event.relatedTarget === menuRef.current) {
            return;
        }
        setVisible.off();
        setFocused.off();
        onBlur?.(event);
    }, [setVisible, setFocused, onBlur]);
    const handleCrossIconClick = React.useCallback(() => setValue(''), [setValue]);
    const handleClose = React.useCallback(() => {
        setVisible.off();
        mobile && inputRef.current?.blur();
    }, [setVisible, mobile]);
    const iconElement = React.useMemo(() => {
        if (icon) {
            return icon;
        }
        if (withCrossIcon && visible && innerValue !== undefined) {
            return React.createElement(Cross, { className: cx('icon'), onClick: handleCrossIconClick });
        }
        return (React.createElement(ArrowDown, { className: cx('icon', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'is-arrow-turned': visible,
            }) }));
    }, [icon, withCrossIcon, visible, innerValue, cx, handleCrossIconClick]);
    React.useEffect(() => {
        if (value !== undefined) {
            setInnerValue(value);
        }
    }, [setInnerValue, value]);
    React.useEffect(() => {
        setInnerOptions(options);
    }, [options]);
    // autoFocus should be applied only once
    React.useEffect(() => {
        autoFocus && inputRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, className: menuClassName, mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixedWidth, mobile: mobile, flip: flip, fullscreen: fullscreen, tabIndex: -1, optionsSort: optionsSort, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect },
        React.createElement("div", { "data-qa": "Select", className: classNames, tabIndex: -1 },
            React.createElement(TextInput, { ...restProps, ref: useMergedRef(ref, inputRef), readOnly: true, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, leftIcon: leftIcon, onFocus: handleFocus, onBlur: handleBlur }))));
});
Select.displayName = 'Select';

export { Select };
//# sourceMappingURL=Select.js.map
